import { useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const formatPrice = (price) => {
  if (price >= 1000000) return `${(price / 1000000).toFixed(1)}M`;
  return `${Math.round(price / 1000)}K`;
};

const createMarkerIcon = (price, isActive = false) => {
  return L.divIcon({
    className: '',
    html: `<div class="marker-pin ${isActive ? 'marker-pin--active' : ''}">${formatPrice(price)}</div>`,
    iconSize: [48, 32],
    iconAnchor: [24, 16],
  });
};

// Map event handler component
const MapEvents = ({ onBoundsChange }) => {
  const map = useMapEvents({
    moveend: () => {
      const bounds = map.getBounds();
      onBoundsChange({
        ne_lat: bounds.getNorthEast().lat,
        ne_lng: bounds.getNorthEast().lng,
        sw_lat: bounds.getSouthWest().lat,
        sw_lng: bounds.getSouthWest().lng,
      });
    },
  });
  return null;
};

// Markers layer component
const MarkersLayer = ({ properties, hoveredId, onPropertyClick }) => {
  const map = useMap();
  const markersRef = useRef([]);

  useEffect(() => {
    // Clear existing markers
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    if (!properties || properties.length === 0) return;

    properties.forEach(prop => {
      if (!prop.lat || !prop.lng) return;
      const marker = L.marker([prop.lat, prop.lng], {
        icon: createMarkerIcon(prop.price, prop.id === hoveredId),
      });

      marker.on('click', () => {
        if (onPropertyClick) onPropertyClick(prop);
      });

      // Popup
      marker.bindPopup(`
        <div style="font-family: 'Work Sans', sans-serif; min-width: 180px;">
          <div style="font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 16px; margin-bottom: 4px;">$${prop.price?.toLocaleString()}</div>
          <div style="font-size: 13px; color: #666; margin-bottom: 4px;">${prop.address}</div>
          <div style="font-size: 12px; color: #888;">${prop.beds} bd | ${prop.baths} ba | ${prop.sqft?.toLocaleString()} sqft</div>
        </div>
      `, { closeButton: false, className: 'custom-popup' });

      marker.addTo(map);
      markersRef.current.push(marker);
    });

    return () => {
      markersRef.current.forEach(m => map.removeLayer(m));
    };
  }, [properties, hoveredId, map, onPropertyClick]);

  return null;
};

export const MapView = ({
  properties = [],
  center = [39.8283, -98.5795],
  zoom = 4,
  onBoundsChange,
  hoveredId,
  onPropertyClick,
  className = '',
}) => {
  return (
    <div className={`rounded-2xl border border-border overflow-hidden shadow-sm ${className}`} data-testid="map-container">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {onBoundsChange && <MapEvents onBoundsChange={onBoundsChange} />}
        <MarkersLayer
          properties={properties}
          hoveredId={hoveredId}
          onPropertyClick={onPropertyClick}
        />
      </MapContainer>
    </div>
  );
};

export default MapView;

